import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SearchIcon, ZapIcon, ShieldIcon, CodeIcon, DollarSignIcon, UsersIcon, LayoutGridIcon, SparklesIcon, LockIcon, TrendingUpIcon, CheckCircleIcon, ArrowRightIcon, GlobeIcon, CreditCardIcon, RocketIcon, FolderIcon, ShareIcon, WrenchIcon } from 'lucide-react';
type Tab = 'discover' | 'build' | 'use';
export const Marketing = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<Tab>('discover');
  const stats = [{
    label: 'Active Apps',
    value: '10,000+'
  }, {
    label: 'Developers',
    value: '50,000+'
  }, {
    label: 'Monthly Users',
    value: '2M+'
  }, {
    label: 'AI Models',
    value: '15+'
  }];
  return <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50" data-id="element-1848">
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-br from-indigo-600 via-blue-600 to-cyan-600" data-id="element-1849">
        <div className="absolute inset-0 bg-grid-white/[0.05] bg-[size:20px_20px]" data-id="element-1850" />
        <div className="relative max-w-7xl mx-auto px-6 py-20 sm:py-24" data-id="element-1851">
          <div className="text-center" data-id="element-1852">
            <h1 className="text-5xl sm:text-6xl font-bold text-white mb-6 tracking-tight" data-id="element-1853">
              The AI App Platform
              <br data-id="element-1854" />
              <span className="text-cyan-200" data-id="element-1855">for Everyone</span>
            </h1>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto mb-8" data-id="element-1856">
              Discover, build, and use AI applications without the complexity.
              One platform, unlimited possibilities.
            </p>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto mt-12" data-id="element-1857">
              {stats.map(stat => <div key={stat.label} className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20" data-id="element-1858">
                  <div className="text-3xl font-bold text-white mb-1" data-id="element-1859">
                    {stat.value}
                  </div>
                  <div className="text-sm text-blue-100" data-id="element-1860">{stat.label}</div>
                </div>)}
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="sticky top-0 z-40 bg-white border-b border-gray-200 shadow-sm" data-id="element-1861">
        <div className="max-w-7xl mx-auto px-6" data-id="element-1862">
          <div className="flex justify-center space-x-1 py-4" data-id="element-1863">
            {[{
            id: 'discover',
            label: 'Discover Apps',
            icon: SearchIcon
          }, {
            id: 'build',
            label: 'Build Apps',
            icon: CodeIcon
          }, {
            id: 'use',
            label: 'Use Apps',
            icon: ZapIcon
          }].map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id as Tab)} className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-medium transition-all ${activeTab === tab.id ? 'bg-indigo-600 text-white shadow-md' : 'text-gray-600 hover:bg-gray-100'}`} data-id="element-1864">
                <tab.icon className="h-5 w-5" data-id="element-1865" />
                <span data-id="element-1866">{tab.label}</span>
              </button>)}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="max-w-7xl mx-auto px-6 py-16" data-id="element-1867">
        {/* Discover Tab */}
        {activeTab === 'discover' && <div className="space-y-20" data-id="element-1868">
            {/* Hero */}
            <div className="text-center max-w-3xl mx-auto" data-id="element-1869">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-2xl mb-6" data-id="element-1870">
                <SearchIcon className="h-8 w-8 text-blue-600" data-id="element-1871" />
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-4" data-id="element-1872">
                Explore Thousands of AI Apps
              </h2>
              <p className="text-xl text-gray-600 mb-8" data-id="element-1873">
                From image generation to code optimization, find the perfect AI
                tool for any task. No setup required.
              </p>
              <button onClick={() => navigate('/')} className="inline-flex items-center px-8 py-4 bg-indigo-600 text-white rounded-xl font-semibold hover:bg-indigo-700 transition-colors shadow-lg hover:shadow-xl" data-id="element-1874">
                Browse App Store
                <ArrowRightIcon className="ml-2 h-5 w-5" data-id="element-1875" />
              </button>
            </div>

            {/* Features Grid */}
            <div className="grid md:grid-cols-3 gap-8" data-id="element-1876">
              <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100" data-id="element-1877">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4" data-id="element-1878">
                  <LayoutGridIcon className="h-6 w-6 text-blue-600" data-id="element-1879" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3" data-id="element-1880">
                  Vast Repository
                </h3>
                <p className="text-gray-600" data-id="element-1881">
                  Access 10,000+ AI applications across every category. From
                  productivity to creativity, we have the tools you need.
                </p>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100" data-id="element-1882">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mb-4" data-id="element-1883">
                  <CreditCardIcon className="h-6 w-6 text-green-600" data-id="element-1884" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3" data-id="element-1885">
                  One Credit System
                </h3>
                <p className="text-gray-600" data-id="element-1886">
                  Use credits across all apps. No more juggling multiple
                  accounts, subscriptions, or API keys for different AI
                  services.
                </p>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100" data-id="element-1887">
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mb-4" data-id="element-1888">
                  <RocketIcon className="h-6 w-6 text-purple-600" data-id="element-1889" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3" data-id="element-1890">
                  Instant Access
                </h3>
                <p className="text-gray-600" data-id="element-1891">
                  Start using any app immediately. No complex setup, no
                  configuration. Just click and create.
                </p>
              </div>
            </div>

            {/* Visual Showcase */}
            <div className="bg-gradient-to-br from-blue-600 to-indigo-600 rounded-3xl p-12 text-white" data-id="element-1892">
              <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-1893">
                <div data-id="element-1894">
                  <h3 className="text-3xl font-bold mb-4" data-id="element-1895">
                    Discover Your Next Favorite Tool
                  </h3>
                  <ul className="space-y-4" data-id="element-1896">
                    <li className="flex items-start" data-id="element-1897">
                      <CheckCircleIcon className="h-6 w-6 mr-3 flex-shrink-0 mt-0.5" data-id="element-1898" />
                      <span className="text-blue-50" data-id="element-1899">
                        Browse by category or search for specific capabilities
                      </span>
                    </li>
                    <li className="flex items-start" data-id="element-1900">
                      <CheckCircleIcon className="h-6 w-6 mr-3 flex-shrink-0 mt-0.5" data-id="element-1901" />
                      <span className="text-blue-50" data-id="element-1902">
                        Read reviews and ratings from real users
                      </span>
                    </li>
                    <li className="flex items-start" data-id="element-1903">
                      <CheckCircleIcon className="h-6 w-6 mr-3 flex-shrink-0 mt-0.5" data-id="element-1904" />
                      <span className="text-blue-50" data-id="element-1905">
                        Try apps instantly with our unified credit system
                      </span>
                    </li>
                    <li className="flex items-start" data-id="element-1906">
                      <CheckCircleIcon className="h-6 w-6 mr-3 flex-shrink-0 mt-0.5" data-id="element-1907" />
                      <span className="text-blue-50" data-id="element-1908">
                        Save favorites to your personal library
                      </span>
                    </li>
                  </ul>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20" data-id="element-1909">
                  <div className="space-y-3" data-id="element-1910">
                    <div className="h-12 bg-white/20 rounded-lg animate-pulse" data-id="element-1911" />
                    <div className="grid grid-cols-3 gap-3" data-id="element-1912">
                      <div className="h-24 bg-white/20 rounded-lg animate-pulse" data-id="element-1913" />
                      <div className="h-24 bg-white/20 rounded-lg animate-pulse" data-id="element-1914" />
                      <div className="h-24 bg-white/20 rounded-lg animate-pulse" data-id="element-1915" />
                    </div>
                    <div className="h-32 bg-white/20 rounded-lg animate-pulse" data-id="element-1916" />
                  </div>
                </div>
              </div>
            </div>
          </div>}

        {/* Build Tab */}
        {activeTab === 'build' && <div className="space-y-20" data-id="element-1917">
            {/* Hero */}
            <div className="text-center max-w-3xl mx-auto" data-id="element-1918">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-2xl mb-6" data-id="element-1919">
                <CodeIcon className="h-8 w-8 text-green-600" data-id="element-1920" />
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-4" data-id="element-1921">
                Build & Monetize AI Apps in Minutes
              </h2>
              <p className="text-xl text-gray-600 mb-8" data-id="element-1922">
                No code required. Use our AI-powered builder to create, deploy,
                and monetize applications instantly.
              </p>
              <button onClick={() => navigate('/create')} className="inline-flex items-center px-8 py-4 bg-green-600 text-white rounded-xl font-semibold hover:bg-green-700 transition-colors shadow-lg hover:shadow-xl" data-id="element-1923">
                Start Building
                <ArrowRightIcon className="ml-2 h-5 w-5" data-id="element-1924" />
              </button>
            </div>

            {/* Features Grid */}
            <div className="grid md:grid-cols-2 gap-8" data-id="element-1925">
              <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100" data-id="element-1926">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4" data-id="element-1927">
                  <SparklesIcon className="h-6 w-6 text-blue-600" data-id="element-1928" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3" data-id="element-1929">
                  AI-Powered Builder
                </h3>
                <p className="text-gray-600 mb-4" data-id="element-1930">
                  Describe your app idea in plain English. Our AI helps you
                  build it through a conversational interface.
                </p>
                <ul className="space-y-2 text-sm text-gray-600" data-id="element-1931">
                  <li className="flex items-center" data-id="element-1932">
                    <CheckCircleIcon className="h-4 w-4 mr-2 text-green-600" data-id="element-1933" />
                    Chat-based app creation
                  </li>
                  <li className="flex items-center" data-id="element-1934">
                    <CheckCircleIcon className="h-4 w-4 mr-2 text-green-600" data-id="element-1935" />
                    Instant preview and testing
                  </li>
                  <li className="flex items-center" data-id="element-1936">
                    <CheckCircleIcon className="h-4 w-4 mr-2 text-green-600" data-id="element-1937" />
                    One-click deployment
                  </li>
                </ul>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100" data-id="element-1938">
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mb-4" data-id="element-1939">
                  <GlobeIcon className="h-6 w-6 text-purple-600" data-id="element-1940" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3" data-id="element-1941">
                  Choose Any AI Model
                </h3>
                <p className="text-gray-600 mb-4" data-id="element-1942">
                  Plug into GPT-4, Claude, Gemini, or any other AI model. Switch
                  between models with a single click.
                </p>
                <ul className="space-y-2 text-sm text-gray-600" data-id="element-1943">
                  <li className="flex items-center" data-id="element-1944">
                    <CheckCircleIcon className="h-4 w-4 mr-2 text-green-600" data-id="element-1945" />
                    15+ AI models supported
                  </li>
                  <li className="flex items-center" data-id="element-1946">
                    <CheckCircleIcon className="h-4 w-4 mr-2 text-green-600" data-id="element-1947" />
                    Easy model comparison
                  </li>
                  <li className="flex items-center" data-id="element-1948">
                    <CheckCircleIcon className="h-4 w-4 mr-2 text-green-600" data-id="element-1949" />
                    Automatic fallbacks
                  </li>
                </ul>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100" data-id="element-1950">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mb-4" data-id="element-1951">
                  <DollarSignIcon className="h-6 w-6 text-green-600" data-id="element-1952" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3" data-id="element-1953">
                  Built-in Monetization
                </h3>
                <p className="text-gray-600 mb-4" data-id="element-1954">
                  Set your price and start earning. We handle payments,
                  distribution, and analytics.
                </p>
                <ul className="space-y-2 text-sm text-gray-600" data-id="element-1955">
                  <li className="flex items-center" data-id="element-1956">
                    <CheckCircleIcon className="h-4 w-4 mr-2 text-green-600" data-id="element-1957" />
                    Flexible pricing models
                  </li>
                  <li className="flex items-center" data-id="element-1958">
                    <CheckCircleIcon className="h-4 w-4 mr-2 text-green-600" data-id="element-1959" />
                    Instant payouts
                  </li>
                  <li className="flex items-center" data-id="element-1960">
                    <CheckCircleIcon className="h-4 w-4 mr-2 text-green-600" data-id="element-1961" />
                    Revenue analytics
                  </li>
                </ul>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100" data-id="element-1962">
                <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center mb-4" data-id="element-1963">
                  <WrenchIcon className="h-6 w-6 text-orange-600" data-id="element-1964" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3" data-id="element-1965">
                  No Code Required
                </h3>
                <p className="text-gray-600 mb-4" data-id="element-1966">
                  Build professional apps without writing a single line of code.
                  Simple forms and visual tools.
                </p>
                <ul className="space-y-2 text-sm text-gray-600" data-id="element-1967">
                  <li className="flex items-center" data-id="element-1968">
                    <CheckCircleIcon className="h-4 w-4 mr-2 text-green-600" data-id="element-1969" />
                    Visual app builder
                  </li>
                  <li className="flex items-center" data-id="element-1970">
                    <CheckCircleIcon className="h-4 w-4 mr-2 text-green-600" data-id="element-1971" />
                    Pre-built templates
                  </li>
                  <li className="flex items-center" data-id="element-1972">
                    <CheckCircleIcon className="h-4 w-4 mr-2 text-green-600" data-id="element-1973" />
                    Easy API integration
                  </li>
                </ul>
              </div>
            </div>

            {/* CTA Section */}
            <div className="bg-gradient-to-br from-green-600 to-emerald-600 rounded-3xl p-12 text-white text-center" data-id="element-1974">
              <h3 className="text-3xl font-bold mb-4" data-id="element-1975">
                Join 50,000+ Developers Building on RealUseAI
              </h3>
              <p className="text-xl text-green-50 mb-8 max-w-2xl mx-auto" data-id="element-1976">
                From idea to published app in minutes. Start building your AI
                business today.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-1977">
                <button onClick={() => navigate('/create')} className="px-8 py-4 bg-white text-green-600 rounded-xl font-semibold hover:bg-green-50 transition-colors shadow-lg" data-id="element-1978">
                  Create Your First App
                </button>
                <button onClick={() => navigate('/apps')} className="px-8 py-4 bg-green-700 text-white rounded-xl font-semibold hover:bg-green-800 transition-colors border-2 border-white/20" data-id="element-1979">
                  View Examples
                </button>
              </div>
            </div>
          </div>}

        {/* Use Tab */}
        {activeTab === 'use' && <div className="space-y-20" data-id="element-1980">
            {/* Hero */}
            <div className="text-center max-w-3xl mx-auto" data-id="element-1981">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-2xl mb-6" data-id="element-1982">
                <ZapIcon className="h-8 w-8 text-purple-600" data-id="element-1983" />
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-4" data-id="element-1984">
                Your Personal AI Workspace
              </h2>
              <p className="text-xl text-gray-600 mb-8" data-id="element-1985">
                Build custom workflows, organize your favorite apps, and keep
                your data secure. All in one place.
              </p>
              <button onClick={() => navigate('/my-apps')} className="inline-flex items-center px-8 py-4 bg-purple-600 text-white rounded-xl font-semibold hover:bg-purple-700 transition-colors shadow-lg hover:shadow-xl" data-id="element-1986">
                Go to My Apps
                <ArrowRightIcon className="ml-2 h-5 w-5" data-id="element-1987" />
              </button>
            </div>

            {/* Features Grid */}
            <div className="grid md:grid-cols-3 gap-8" data-id="element-1988">
              <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100" data-id="element-1989">
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mb-4" data-id="element-1990">
                  <FolderIcon className="h-6 w-6 text-purple-600" data-id="element-1991" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3" data-id="element-1992">
                  Personal Library
                </h3>
                <p className="text-gray-600" data-id="element-1993">
                  Save and organize your favorite apps. Access them instantly
                  from your personal dashboard.
                </p>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100" data-id="element-1994">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4" data-id="element-1995">
                  <WrenchIcon className="h-6 w-6 text-blue-600" data-id="element-1996" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3" data-id="element-1997">
                  Custom Workflows
                </h3>
                <p className="text-gray-600" data-id="element-1998">
                  Build apps tailored to your specific needs. Modify existing
                  apps or create new ones from scratch.
                </p>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100" data-id="element-1999">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mb-4" data-id="element-2000">
                  <ShareIcon className="h-6 w-6 text-green-600" data-id="element-2001" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3" data-id="element-2002">
                  Share & Collaborate
                </h3>
                <p className="text-gray-600" data-id="element-2003">
                  Share your custom apps with team members or the community.
                  Collaborate on workflows.
                </p>
              </div>
            </div>

            {/* Privacy Section */}
            <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-3xl p-12 text-white" data-id="element-2004">
              <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-2005">
                <div data-id="element-2006">
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-white/10 rounded-xl mb-6" data-id="element-2007">
                    <ShieldIcon className="h-6 w-6 text-white" data-id="element-2008" />
                  </div>
                  <h3 className="text-3xl font-bold mb-4" data-id="element-2009">
                    Your Data, Your Control
                  </h3>
                  <p className="text-slate-300 mb-6" data-id="element-2010">
                    We take privacy seriously. Your data is encrypted,
                    protected, and never shared without your permission.
                  </p>
                  <ul className="space-y-3" data-id="element-2011">
                    <li className="flex items-start" data-id="element-2012">
                      <LockIcon className="h-5 w-5 mr-3 flex-shrink-0 mt-0.5 text-green-400" data-id="element-2013" />
                      <span className="text-slate-200" data-id="element-2014">
                        End-to-end encryption for all data
                      </span>
                    </li>
                    <li className="flex items-start" data-id="element-2015">
                      <LockIcon className="h-5 w-5 mr-3 flex-shrink-0 mt-0.5 text-green-400" data-id="element-2016" />
                      <span className="text-slate-200" data-id="element-2017">
                        Private apps stay private by default
                      </span>
                    </li>
                    <li className="flex items-start" data-id="element-2018">
                      <LockIcon className="h-5 w-5 mr-3 flex-shrink-0 mt-0.5 text-green-400" data-id="element-2019" />
                      <span className="text-slate-200" data-id="element-2020">
                        Full control over data sharing and permissions
                      </span>
                    </li>
                    <li className="flex items-start" data-id="element-2021">
                      <LockIcon className="h-5 w-5 mr-3 flex-shrink-0 mt-0.5 text-green-400" data-id="element-2022" />
                      <span className="text-slate-200" data-id="element-2023">
                        GDPR and SOC 2 compliant
                      </span>
                    </li>
                  </ul>
                </div>
                <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10" data-id="element-2024">
                  <div className="space-y-4" data-id="element-2025">
                    <div className="flex items-center justify-between p-4 bg-white/10 rounded-lg" data-id="element-2026">
                      <span className="text-sm" data-id="element-2027">Data Encryption</span>
                      <CheckCircleIcon className="h-5 w-5 text-green-400" data-id="element-2028" />
                    </div>
                    <div className="flex items-center justify-between p-4 bg-white/10 rounded-lg" data-id="element-2029">
                      <span className="text-sm" data-id="element-2030">Private by Default</span>
                      <CheckCircleIcon className="h-5 w-5 text-green-400" data-id="element-2031" />
                    </div>
                    <div className="flex items-center justify-between p-4 bg-white/10 rounded-lg" data-id="element-2032">
                      <span className="text-sm" data-id="element-2033">Access Controls</span>
                      <CheckCircleIcon className="h-5 w-5 text-green-400" data-id="element-2034" />
                    </div>
                    <div className="flex items-center justify-between p-4 bg-white/10 rounded-lg" data-id="element-2035">
                      <span className="text-sm" data-id="element-2036">Compliance Ready</span>
                      <CheckCircleIcon className="h-5 w-5 text-green-400" data-id="element-2037" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Features List */}
            <div className="grid md:grid-cols-2 gap-8" data-id="element-2038">
              <div className="space-y-6" data-id="element-2039">
                <h3 className="text-2xl font-bold text-gray-900" data-id="element-2040">
                  Productivity Features
                </h3>
                <div className="space-y-4" data-id="element-2041">
                  <div className="flex items-start" data-id="element-2042">
                    <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0" data-id="element-2043">
                      <CheckCircleIcon className="h-5 w-5 text-purple-600" data-id="element-2044" />
                    </div>
                    <div data-id="element-2045">
                      <h4 className="font-semibold text-gray-900 mb-1" data-id="element-2046">
                        Quick Access Dashboard
                      </h4>
                      <p className="text-gray-600 text-sm" data-id="element-2047">
                        All your apps in one place with smart organization
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start" data-id="element-2048">
                    <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0" data-id="element-2049">
                      <CheckCircleIcon className="h-5 w-5 text-purple-600" data-id="element-2050" />
                    </div>
                    <div data-id="element-2051">
                      <h4 className="font-semibold text-gray-900 mb-1" data-id="element-2052">
                        Usage History
                      </h4>
                      <p className="text-gray-600 text-sm" data-id="element-2053">
                        Track your interactions and revisit previous work
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start" data-id="element-2054">
                    <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0" data-id="element-2055">
                      <CheckCircleIcon className="h-5 w-5 text-purple-600" data-id="element-2056" />
                    </div>
                    <div data-id="element-2057">
                      <h4 className="font-semibold text-gray-900 mb-1" data-id="element-2058">
                        Custom Categories
                      </h4>
                      <p className="text-gray-600 text-sm" data-id="element-2059">
                        Organize apps your way with tags and folders
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-6" data-id="element-2060">
                <h3 className="text-2xl font-bold text-gray-900" data-id="element-2061">
                  Customization Options
                </h3>
                <div className="space-y-4" data-id="element-2062">
                  <div className="flex items-start" data-id="element-2063">
                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0" data-id="element-2064">
                      <CheckCircleIcon className="h-5 w-5 text-blue-600" data-id="element-2065" />
                    </div>
                    <div data-id="element-2066">
                      <h4 className="font-semibold text-gray-900 mb-1" data-id="element-2067">
                        Modify Any App
                      </h4>
                      <p className="text-gray-600 text-sm" data-id="element-2068">
                        Fork and customize existing apps to fit your needs
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start" data-id="element-2069">
                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0" data-id="element-2070">
                      <CheckCircleIcon className="h-5 w-5 text-blue-600" data-id="element-2071" />
                    </div>
                    <div data-id="element-2072">
                      <h4 className="font-semibold text-gray-900 mb-1" data-id="element-2073">
                        Build Private Apps
                      </h4>
                      <p className="text-gray-600 text-sm" data-id="element-2074">
                        Create apps just for yourself or your team
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start" data-id="element-2075">
                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0" data-id="element-2076">
                      <CheckCircleIcon className="h-5 w-5 text-blue-600" data-id="element-2077" />
                    </div>
                    <div data-id="element-2078">
                      <h4 className="font-semibold text-gray-900 mb-1" data-id="element-2079">
                        Workflow Automation
                      </h4>
                      <p className="text-gray-600 text-sm" data-id="element-2080">
                        Chain apps together for powerful workflows
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>}
      </div>

      {/* Final CTA */}
      <div className="bg-gradient-to-br from-indigo-600 via-blue-600 to-cyan-600 py-20" data-id="element-2081">
        <div className="max-w-4xl mx-auto px-6 text-center" data-id="element-2082">
          <h2 className="text-4xl font-bold text-white mb-6" data-id="element-2083">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-blue-100 mb-8" data-id="element-2084">
            Join millions of users discovering, building, and using AI apps on
            RealUseAI
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-2085">
            <button onClick={() => navigate('/')} className="px-8 py-4 bg-white text-indigo-600 rounded-xl font-semibold hover:bg-blue-50 transition-colors shadow-lg" data-id="element-2086">
              Explore Apps
            </button>
            <button onClick={() => navigate('/create')} className="px-8 py-4 bg-indigo-700 text-white rounded-xl font-semibold hover:bg-indigo-800 transition-colors border-2 border-white/20" data-id="element-2087">
              Start Building
            </button>
          </div>
        </div>
      </div>
    </div>;
};